import type { CarrierCatalogueEntry, RouteCatalogueEntry } from '@my/directory-api';
import type { FerryRoute, FerryRouteCategory } from '@my/ferry-api';

// ────────────────────────────────────────────────────────────────────────────
// The suite's crossing list in one shape, from the catalogue file Directory
// bundles.
//
// A pick is keyed either by the file (`route.dub_hol.stena_line`, both
// directions) or, for a carrier booked through My.Ferry, by Ferry's codes
// (`route.stena.duho` one way, `carrier.stena`). Directory never calls Ferry:
// it accepts a Ferry key only when the file links it (the file's
// `ferryCarrierCode` and `ferryRouteCode` / `ferryReverseRouteCode`) and
// resolves the key to that file entry. This list does the same, and the web
// never calls Ferry for it either: a Ferry-keyed entry takes its port names,
// codes, countries and typical minutes from the file entry, swapped for a
// reverse leg, and carries no Ferry ids — `useFerryCrossing` asks Ferry about
// a crossing only when a booking needs it.
//
// Every entry is the Directory `RouteCatalogueEntry` shape the booking form,
// the journey planner and the Settings cards already read, plus the link.
// ────────────────────────────────────────────────────────────────────────────

export type CatalogueSource = 'file' | 'ferry';

export type CrossingRoute = RouteCatalogueEntry & {
  source: CatalogueSource;
  /** Directory will accept the key: a file entry always; a Ferry entry once the file links it. */
  linked: boolean;
  /** Ferry's code for the entry's direction; null when the carrier is not booked through Ferry. */
  ferryRouteCode: string | null;
  /** The flipped leg's code — a file entry stands for both directions. */
  ferryReverseRouteCode: string | null;
  /** A file entry is sailed both ways; a Ferry route is one direction. */
  bidirectional: boolean;
  /** Ferry's sea area; null from the catalogue. */
  category: FerryRouteCategory | null;
  /** Ferry's route when the caller already holds it; null from the catalogue. */
  ferryRoute: FerryRoute | null;
};

/**
 * One TMS booking type at one carrier, from the carrier file: the old
 * desktop's ShippingIntegration table in our shape.
 */
export interface FerryVehicleTypeMapping {
  /** The carrier's vehicle type code, e.g. Stena `AR`. */
  code: string;
  /** The code when the driver crosses with the unit, for a type that goes either way. */
  accompaniedCode?: string | null;
  /** The code for temperature-controlled cargo. */
  reeferCode?: string | null;
  /** Sent with the booking when the carrier's type has no default length. */
  lengthMetres?: number | null;
}

/**
 * Keyed by an equipment catalogue key or one of its prefixes
 * (`trailer.reefer.single_temp`, `trailer.reefer`, `trailer`), or by a TMS
 * booking type name (`Artic`, `Trailer`, …) as the fallback.
 */
export type FerryVehicleTypeMap = Record<string, FerryVehicleTypeMapping>;

export type CrossingCarrier = CarrierCatalogueEntry & {
  source: CatalogueSource;
  /** Directory will accept the key: a file entry always; a Ferry entry once the file links it. */
  linked: boolean;
  /** The same carrier under the other key style: a file carrier's Ferry key, a Ferry carrier's file key. Null when unlinked. */
  linkedKey: string | null;
  ferryCarrierCode: string | null;
  ferryCarrierId: string | null;
  /** Which of the carrier's vehicle types a booking type books as; null when the file has none for it. */
  ferryVehicleTypes: FerryVehicleTypeMap | null;
};

/** The file route a Ferry route resolves to, and whether the Ferry route is its reverse leg. */
export interface FileRouteLink {
  entry: CrossingRoute;
  reversed: boolean;
}

export function ferryRouteKey(carrierCode: string, routeCode: string): string {
  return `route.${carrierCode.toLowerCase()}.${routeCode.toLowerCase()}`;
}

export function ferryCarrierKey(carrierCode: string): string {
  return `carrier.${carrierCode.toLowerCase()}`;
}

// The three link fields are on the Directory API since 19.10.26 but not yet in
// the generated `@my/directory-api` types; read through this until the schema
// is regenerated, then drop it.
type FileLinks = {
  ferryRouteCode?: string | null;
  ferryReverseRouteCode?: string | null;
  ferryCarrierCode?: string | null;
  ferryVehicleTypes?: FerryVehicleTypeMap | null;
};

export function fileRouteEntry(entry: RouteCatalogueEntry): CrossingRoute {
  const links = entry as RouteCatalogueEntry & FileLinks;
  return {
    ...entry,
    source: 'file',
    linked: true,
    ferryRouteCode: links.ferryRouteCode?.trim() || null,
    ferryReverseRouteCode: links.ferryReverseRouteCode?.trim() || null,
    bidirectional: true,
    category: null,
    ferryRoute: null,
  };
}

export function fileCarrierEntry(entry: CarrierCatalogueEntry): CrossingCarrier {
  const links = entry as CarrierCatalogueEntry & FileLinks;
  const ferryCarrierCode = links.ferryCarrierCode?.trim() || null;
  const vehicleTypes = links.ferryVehicleTypes ?? null;
  return {
    ...entry,
    source: 'file',
    linked: true,
    linkedKey: ferryCarrierCode ? ferryCarrierKey(ferryCarrierCode) : null,
    ferryCarrierCode,
    ferryCarrierId: null,
    ferryVehicleTypes: vehicleTypes && Object.keys(vehicleTypes).length ? vehicleTypes : null,
  };
}

/** The file entry's side of a linked leg: its ports, countries, codes and minutes, swapped for a reverse leg. */
function linkedLegFields(file: FileRouteLink) {
  const e = file.entry;
  const r = file.reversed;
  const originPortName = r ? e.destinationPortName : e.originPortName;
  const destinationPortName = r ? e.originPortName : e.destinationPortName;
  return {
    name: `${originPortName} → ${destinationPortName}`,
    originPortName,
    originCountryIsoCode: r ? e.destinationCountryIsoCode : e.originCountryIsoCode,
    originCustomsTerritory: (r ? e.destinationCustomsTerritory : e.originCustomsTerritory) ?? null,
    originPortCode: (r ? e.destinationPortCode : e.originPortCode) ?? null,
    destinationPortName,
    destinationCountryIsoCode: r ? e.originCountryIsoCode : e.destinationCountryIsoCode,
    destinationCustomsTerritory:
      (r ? e.originCustomsTerritory : e.destinationCustomsTerritory) ?? null,
    destinationPortCode: (r ? e.originPortCode : e.destinationPortCode) ?? null,
    typicalDurationMinutes: e.typicalDurationMinutes ?? null,
    typicalCheckInMinutes: e.typicalCheckInMinutes ?? null,
    typicalDriveOffMinutes: e.typicalDriveOffMinutes ?? null,
    accompaniment: e.accompaniment ?? null,
  };
}

/**
 * The file's carrier under its Ferry key (`carrier.stena`), as Directory
 * resolves that key: the file's code and name, no Ferry id. Null when the
 * file does not link the carrier.
 */
export function fileLinkedFerryCarrierEntry(file: CrossingCarrier): CrossingCarrier | null {
  if (!file.ferryCarrierCode) return null;
  return {
    ...file,
    key: ferryCarrierKey(file.ferryCarrierCode),
    source: 'ferry',
    linked: true,
    linkedKey: file.key,
    ferryCarrierId: null,
  };
}

/**
 * The file's route as its Ferry-keyed legs, one per linked code (the reverse
 * code as the flipped leg): `route.stena.duho` and `route.stena.hodu`. The
 * leg's ports and minutes are the file entry's; no Ferry route is behind it.
 */
export function fileLinkedFerryRouteEntries(
  file: CrossingRoute,
  carrierCode: string,
): CrossingRoute[] {
  const legs: Array<[code: string, reversed: boolean]> = [];
  if (file.ferryRouteCode) legs.push([file.ferryRouteCode, false]);
  if (file.ferryReverseRouteCode) legs.push([file.ferryReverseRouteCode, true]);
  return legs.map(([code, reversed]) => ({
    key: ferryRouteKey(carrierCode, code),
    code,
    carrierKey: ferryCarrierKey(carrierCode),
    source: 'ferry' as const,
    linked: true,
    ferryRouteCode: code,
    ferryReverseRouteCode: null,
    bidirectional: false,
    category: null,
    ferryRoute: null,
    ...linkedLegFields({ entry: file, reversed }),
  }));
}

/** The Ferry route code for one leg of a crossing, or null when the carrier is not booked through Ferry. */
export function ferryRouteCodeFor(
  entry: CrossingRoute | null | undefined,
  reversed: boolean,
): string | null {
  const code = reversed ? entry?.ferryReverseRouteCode : entry?.ferryRouteCode;
  return code?.trim() || null;
}

export function ferryCarrierCodeFor(entry: CrossingCarrier | null | undefined): string | null {
  return entry?.ferryCarrierCode?.trim() || null;
}
